import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import { useGetRepairTicketByIdQuery } from '../../../store/api/repairApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import InvoiceBarcode from '../../../shared/components/InvoiceBarcode';

export default function RepairTicketPrint() {
  const { id } = useParams();
  const { data: ticket, isLoading } = useGetRepairTicketByIdQuery(id);
  const { data: company, isLoading: companyLoading } = useGetCurrentCompanyQuery();

  useEffect(() => {
    if (!isLoading && !companyLoading && ticket) {
      setTimeout(() => {
        window.print();
      }, 500);
    }
  }, [isLoading, companyLoading, ticket]);

  if (isLoading || companyLoading || !ticket) return null;

  const currency = company?.currency || 'USD';
  const totalPartsCost = ticket.items?.reduce((sum, item) => sum + parseFloat(item.subTotal), 0) || 0;
  const laborCharge = parseFloat(ticket.laborCharge) || 0;
  const totalCost = totalPartsCost + laborCharge;

  return (
    <Box sx={{ p: 4, maxWidth: '800px', mx: 'auto', backgroundColor: '#fff' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          {company?.name || 'Company Name'}
        </Typography>
        <Typography variant="body1">{company?.address}</Typography>
        <Typography variant="body1">Phone: {company?.phone}</Typography>
        {company?.trn && <Typography variant="body1">TRN: {company?.trn}</Typography>}
      </Box>

      <Typography variant="h5" align="center" sx={{ mb: 4, textDecoration: 'underline' }}>
        REPAIR TICKET
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body1"><strong>Ticket No:</strong> {ticket.ticketNumber}</Typography>
          <Box sx={{ my: 0.5 }}>
            <InvoiceBarcode value={ticket.ticketNumber} height={28} width={1.2} />
          </Box>
          <Typography variant="body1"><strong>Date:</strong> {new Date(ticket.createdAt).toLocaleString()}</Typography>
          <Typography variant="body1"><strong>Status:</strong> {ticket.status.replace(/_/g, ' ').toUpperCase()}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body1"><strong>Customer:</strong> {ticket.customer?.name}</Typography>
          <Typography variant="body1"><strong>Phone:</strong> {ticket.customer?.phone}</Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ borderBottom: '1px solid #ccc', mb: 2 }}>Device Details</Typography>
        <Typography variant="body1"><strong>Model:</strong> {ticket.deviceModel}</Typography>
        <Typography variant="body1"><strong>IMEI / Serial:</strong> {ticket.deviceImei || '-'}</Typography>
        <Typography variant="body1"><strong>Fault Description:</strong> {ticket.faultDescription}</Typography>
        <Typography variant="body1"><strong>Device Condition:</strong> {ticket.deviceCondition}</Typography>
      </Box>

      {ticket.items && ticket.items.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Spare Parts Used</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Part Description</strong></TableCell>
                  <TableCell align="right"><strong>Qty</strong></TableCell>
                  <TableCell align="right"><strong>Price ({currency})</strong></TableCell>
                  <TableCell align="right"><strong>Total ({currency})</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ticket.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.product?.name}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{parseFloat(item.unitPrice).toFixed(2)}</TableCell>
                    <TableCell align="right">{parseFloat(item.subTotal).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box sx={{ ml: 'auto', width: '50%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">Estimated Cost:</Typography>
          <Typography variant="body1">{parseFloat(ticket.estimatedCost).toFixed(2)} {currency}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">Total Parts Cost:</Typography>
          <Typography variant="body1">{totalPartsCost.toFixed(2)} {currency}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">Labor Charge:</Typography>
          <Typography variant="body1">{laborCharge.toFixed(2)} {currency}</Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6"><strong>Grand Total:</strong></Typography>
          <Typography variant="h6"><strong>{totalCost.toFixed(2)} {currency}</strong></Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 8, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body1">Customer Signature: ______________________</Typography>
        <Typography variant="body1">Technician Signature: ______________________</Typography>
      </Box>

    </Box>
  );
}
